import { Box, useTheme } from "@mui/material";
import { Card, TextField, Typography, palette } from "@powerdiary/components-library/lib";
import { StickyNote } from "lucide-react";
import { useState } from "react";
import styles from "../ConfirmationDetailCard/ConfirmationDetailCard.module.css";

export interface IConfirmationNotesCardProps {
    notes: string;
    isBookingCompleted: boolean;
    onNotesChange: (notes: string) => void;
}

export default function ConfirmationNotesCard({
    notes,
    isBookingCompleted,
    onNotesChange,
}: Readonly<IConfirmationNotesCardProps>) {
    const theme = useTheme();
    const [editableContent, setEditableContent] = useState<boolean>(false);
    const [localNotes, setLocalNotes] = useState<string>(notes);

    const onCardClick = () => {
        if (isBookingCompleted) return;
        if (!editableContent) {
            setEditableContent(true);
            setLocalNotes(notes);
        }
    };

    const handleNotesInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalNotes(event.target.value);
    };

    const handleBlur = () => {
        onNotesChange(localNotes);
        setEditableContent(false);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Escape") {
            setLocalNotes(notes);
            setEditableContent(false);
        }
    };

    const displayContent = notes || "Add notes";

    if (isBookingCompleted && !notes) return null;

    return (
        <Card
            onClick={onCardClick}
            className={`${styles.wrapper} ${isBookingCompleted ? "" : styles.clickable}`}
            sx={{ backgroundColor: palette.neutral["50"], padding: "12px" }}
            data-test-id="section_confirmation_notes_card"
        >
            <Box className={styles.contentWrapper}>
                <Box className={styles.iconContainer} data-test-id="icon_confirmation_notes_card">
                    <StickyNote
                        className={styles.icon}
                        color={theme.palette.primary.main}
                        size={20}
                        strokeWidth={2}
                        aria-hidden
                    />
                </Box>

                <Box className={styles.textContainer}>
                    <Typography
                        variant="paragraphSmallRegular"
                        className={styles.title}
                        data-test-id="text_confirmation_notes_card_title"
                    >
                        Notes
                    </Typography>
                    {editableContent && !isBookingCompleted ? (
                        <TextField
                            value={localNotes}
                            onChange={handleNotesInputChange}
                            onBlur={handleBlur}
                            onKeyDown={handleKeyDown}
                            sx={{ background: "white", borderRadius: "4px" }}
                            multiline
                            rows={3}
                            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
                            data-test-id="textfield_confirmation_notes_card"
                        />
                    ) : (
                        <Typography
                            variant="paragraphMediumSemiBold"
                            color={notes ? "textPrimary" : "primary"}
                            className={styles.content}
                            data-test-id="text_confirmation_notes_card_content"
                            sx={{
                                whiteSpace: "pre-wrap",
                                wordWrap: "break-word",
                            }}
                        >
                            {displayContent}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Card>
    );
}
